import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  Typography,
} from "@mui/material";
import PowerSharpIcon from "@mui/icons-material/PowerSharp";
import LastUpdateTooltip from "../Helpers/LastUpdateTooltip";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import classes from "./TCIV2.module.css";
import applicationUsageLog from "../../../utils/applicationUsagesLog";
import { SnackBarStoreAction } from "Store/SnackBar";
import SnackBar from "components/SnackBar/SnackBar";
import { getTciData, saveTciData } from "services/userServices";
import moment from "moment";
import BackdropComponent from "../../Spinner/Spinner";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { bulkEditStoreAction } from "../../../Store/BulkEdit";

const getFilteredData = (data, property, value) =>
  data.filter((item) => item[property] === value);

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  marginTop: "-10px",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));
const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  marginTop: "-10px",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

const formatDate = (date) =>
  moment(date ? new Date(date) : new Date("yyyy-MM-ddThh:mm")).format(
    "YYYY-MM-DD",
  );

const formatTime = (time) => moment(time || "00:00", "hh:mm A").format("HH:mm");

const validateDateTime = (date, time) => {
  if (date) {
    return formatDate(date) + "T" + formatTime(time);
  }
  return "YYYY-MM-DDTHH:mm";
};

const validateDateFormat = (date) =>
  date !== "YYYY-MM-DDTHH:mm"
    ? moment(new Date(date)).format("YYYY-MM-DD")
    : "";

const validateTimeFormat = (time) =>
  time !== "YYYY-MM-DDTHH:mm" ? moment(new Date(time)).format("HH:mm") : "";

const validateString = (value) => (value != null ? value.toString() : "");

const TCIV2 = (props) => {
  const { t } = useTranslation(["TCI"]);
  const dispatch = useDispatch();
  const [tciData, setTciData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState("ASC");
  const [asdData, setAsdData] = useState("");

  const [agentComments, setAgentComments] = useState("");
  const [filterData, setFilterData] = useState([]);

  const [allData, setAllData] = useState([]);
  const [totalActiveCount, setTotalActiveCount] = useState({
    total: 0,
    active: 0,
  });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const initialRender = useRef(true);
  const classTraineeId = useSelector((state) => state.bulkEdit);
  const { classRequestId, classRosterId } = classTraineeId;
  const navigate = useNavigate();

  const sorting = (col) => {
    if (order === "ASC") {
      const sorted = [...tciData].sort((a, b) => {
        if (a[col]?.toLowerCase() === b[col]?.toLowerCase()) return 0;
        return a[col]?.toLowerCase() > b[col]?.toLowerCase() ? 1 : -1;
      });
      setTciData(sorted);
      setOrder("DSC");
    }
    if (order === "DSC") {
      const sorted = [...tciData].sort((a, b) => {
        if (a[col]?.toLowerCase() === b[col]?.toLowerCase()) return 0;
        return a[col]?.toLowerCase() < b[col]?.toLowerCase() ? 1 : -1;
      });
      setTciData(sorted);
      setOrder("ASC");
    }
  };

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await fetchData();

        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "ASTT",
            "TCI",
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialize:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const refreshData = async () => {
      if (props.resetStatus === true) {
        try {
          await fetchData();
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to refresh data:", error);
          }
        }
      }
    };

    refreshData();

    return () => {
      cancelled = true;
    };
  }, [props.resetStatus]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      setLoading(true);

      const timeout = setTimeout(() => {
        filtering(props.filterStatus, filterData);
        setLoading(false);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [props.filterStatus]);

  const filtering = (filter, data) => {
    let filterData = [];

    if (filter === "ALL") {
      filterData = data;
    } else {
      filterData = data.filter(
        (item) => item.TraineeStatus === props.filterStatus,
      );
    }
    setTciData(filterData);
    initialCheck(filterData);
    props.closeLoader();
  };

  const initialCheck = (data) => {
    let activeCount = getFilteredData(data, "TraineeStatus", "Active");
    setTotalActiveCount({
      total: data.length,
      active: activeCount.length,
    });
  };

  const getPERRExtAssigned = (item) => {
    const { PERRExtAssignedOverride, PERRExtAssigned } = item;
    if (PERRExtAssignedOverride === null || PERRExtAssignedOverride === "") {
      if (PERRExtAssigned === null) {
        return null;
      }
      return PERRExtAssigned === "YES";
    }
    return PERRExtAssignedOverride === "YES";
  };

  const getPERRExtReason = (item) => {
    const { PERRExtReasonOverride, PERRExtReason } = item;

    if (PERRExtReasonOverride === null || PERRExtReasonOverride === "") {
      return PERRExtReason;
    }
    return PERRExtReasonOverride;
  };

  const getBuildRebuildStick = (item) => {
    const { BuildRebuildStickOverride, BuildRebuildStick } = item;
    if (BuildRebuildStickOverride === null || BuildRebuildStick === "") {
      return BuildRebuildStick;
    }
    return BuildRebuildStickOverride;
  };

  const prepareTCIDataFromResponse = (responseData) => {
    return responseData.map((itm) => ({
      ...itm,
      PERRExtAssigned: getPERRExtAssigned(itm),
      PERRExtReason: getPERRExtReason(itm),
      BuildRebuildStick: getBuildRebuildStick(itm),
      TCIdate: validateDateTime(itm.TCIDate, itm.TCITime),
      TCIRecovery: validateDateTime(itm.TCIRecoveryDate, itm.TCIRecoveryTime),
    }));
  };

  const fetchData = async () => {
    setLoading(true);

    let obj = {
      classTraineeIdList: classTraineeId.traineeDetails,
    };
    const response = await getTciData(obj);
    if (response.message === "Success") {
      let data = prepareTCIDataFromResponse(response.data);

      setAllData(data);
      setTciData(data);
      setFilterData(data);
      dispatch(bulkEditStoreAction.setTciData(data));
      dispatch(bulkEditStoreAction.setTciAllData(data));

      if (props.resetStatus === true) {
        setTciData(data);
        setFilterData(data);
        props.onChange();
        setAsdData("");
        setAgentComments("");
      }

      if (response.data) {
        filtering(props.filterStatus, data);
      }
    } else {
      setSnack({
        open: true,
        message: "Failed to load data!",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handleAll = (eventValue, key) => {
    let data = [];
    let updatedTciData = [];

    if (eventValue === "" || eventValue === " ") {
      data = filterData
        .map((item, i) => {
          if (shouldUpdateKey(item, i)) {
            return {
              ...item,
              [key]: allData[i][key],
            };
          }
          return null;
        })
        .filter(Boolean);

      updatedTciData = tciData.map((item) => {
        const initialData = findInitialData(item.OracleId);

        return {
          ...item,
          [key]: initialData[key],
        };
      });
    } else {
      data = filterData.map((item) => {
        if (
          props.filterStatus === "ALL" ||
          props.filterStatus === item.TraineeStatus
        )
          return {
            ...item,
            [key]: eventValue,
          };
        else return { ...item };
      });

      updatedTciData = tciData.map((item) => {
        return {
          ...item,
          [key]: eventValue,
        };
      });
    }
    setTciData(updatedTciData);
    setFilterData(data);
    dispatch(bulkEditStoreAction.setTciData(updatedTciData));
  };

  const shouldUpdateKey = (item, index) => {
    return (
      (props.filterStatus === "ALL" &&
        item.OracleId === allData[index].OracleId) ||
      (props.filterStatus !== "ALL" &&
        item.TraineeStatus === props.filterStatus)
    );
  };

  const findInitialData = (oracleId) => {
    return allData.find((item) => item.OracleId === oracleId);
  };

  const renderTextCell = (nameWithId, traineeStatus, isInactive) => (
    <Grid item xs={0.6} align="center">
      <p className={isInactive ? "text-decoration-line-bulk" : null}>
        {nameWithId}
      </p>
      {isInactive && <p className={classes.blueColor}>{traineeStatus}</p>}
    </Grid>
  );

  const handleIndividualText = (event, row, key) => {
    let data = filterData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [key]: event.target.value,
        };
      } else {
        return item;
      }
    });
    let trainee = tciData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [key]: event.target.value === "" ? null : event.target.value,
        };
      } else {
        return item;
      }
    });
    setTciData(trainee);
    setFilterData(data);
    dispatch(bulkEditStoreAction.setTciData(trainee));
  };

  const handleChangeChecked = (event, row, i) => {
    let data = filterData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [event.target.name]:
            event.target.checked === true ? event.target.checked : null,
        };
      } else {
        return item;
      }
    });

    let trainee = tciData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [event.target.name]:
            event.target.checked === true ? event.target.checked : null,
        };
      } else {
        return item;
      }
    });
    setTciData(trainee);
    setFilterData(data);
    dispatch(bulkEditStoreAction.setTciData(trainee));
  };

  const saveCloseHandler = async (e) => {
    setLoading(true);

    const obj = createData();
    if (obj.length === tciData.length) {
      const response = await saveTciData(obj);
      if (response.message === "Success") {
        const obj = {
          open: true,
          message: "Update completed successfully.",
          severity: "success",
        };
        dispatch(bulkEditStoreAction.setPreviousTabName(""));
        dispatch(SnackBarStoreAction.updateSnack(obj));
        navigateBack();
      } else {
        setSnack({
          open: true,
          message: "Failed to apply changes.",
          severity: "error",
        });
      }
    }
    setLoading(false);
  };

  const saveHandler = async (e) => {
    setLoading(true);

    const obj = createData();
    if (obj.length === tciData.length) {
      const response = await saveTciData(obj);
      if (response.message === "Success") {
        setSnack({
          open: true,
          message: "Update completed successfully.",
          severity: "success",
        });
        setAllData(filterData);
        setLoading(false);
        setAgentComments("");
        setAsdData("");

        fetchData();
      } else {
        setLoading(false);
        setSnack({
          open: true,
          message: "Failed to apply changes.",
          severity: "error",
        });
      }
    }
    setLoading(false);
  };

  const getPERRExtAssignedForPayload = (element) => {
    if (element.PERRExtAssigned === null) {
      return null;
    } else if (element.PERRExtAssigned === true) {
      return "YES";
    }
    return "NO";
  };

  const createData = () => {
    //tciData contains only filtered data
    const obj = [];
    for (const element of tciData) {
      if (element.TCIdate === "" || element.TCIRecovery === "") {
        setSnack({
          open: true,
          message: `Invalid date at Oracle Id : ${element.OracleId}`,
          severity: "error",
        });
        break;
      } else
        obj.push({
          agentComments: element.AgentComments,
          asdLead: element.ASDLead,
          buildRebuildStick: element.BuildRebuildStick,
          classTraineeId: element.ClassTraineeId,
          perrExtAssigned: getPERRExtAssignedForPayload(element),
          perrExtReason: element.PERRExtReason,
          tciDate: validateDateFormat(element.TCIdate),
          tciRecoveryDate: validateDateFormat(element.TCIRecovery),
          tciRecoveryTime: validateTimeFormat(element.TCIRecovery),
          tciRecoveryZoomLink: element.TCIRecoveryZoomLink,
          tciRecoveryZoomPassword: element.TCIRecoveryZoomPassword,
          tciTime: validateTimeFormat(element.TCIdate),
          tciZoomLink: element.TCIZoomLink,
          tciZoomPassword: element.TCIZoomPassword,
          workedHoursTCI: validateString(element.WorkedHoursTCI),
          workedHoursTCIRecovery: validateString(
            element.WorkedHoursTCIRecovery,
          ),
          modifiedBy: localStorage.getItem("User"),
        });
    }

    return obj;
  };

  const navigateBack = () => {
    navigate(
      `/home/searchrosters?classRequestId=${classRequestId}&classRosterId=${classRosterId}`,
    );
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <TableContainer className={classes.container} component={Paper}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell
                className={"text-center align-center"}
                align="center"
                sx={{
                  fontFamily: "Urbanist",
                  fontWeight: "bold",
                  fontSize: "16px",
                  lineHeight: "24px",
                  letterSpacing: "0%",
                  backgroundColor: "#e5e4e2",
                  color: "#3F5765",
                  minWidth: "300px !important",
                  position: "sticky",
                  left: 0,
                  top: "0 !important",
                  zIndex: "1100 !important",
                  boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
                }}
                onClick={() => sorting("Name")}
              >
                <span>{t("name")}</span>
                <Box sx={{ display: "inline-block" }}>
                  <ArrowUpwardIcon
                    className={`${classes.arrowIcon} ${
                      order === "ASC" ? classes.arrowAsc : classes.arrowInactive
                    }`}
                  />
                  <ArrowDownwardIcon
                    className={`${classes.arrowIcon} ${
                      order === "DSC"
                        ? classes.arrowDesc
                        : classes.arrowInactive
                    }`}
                  />
                </Box>
                <Typography
                  className={classes.blueColor}
                  sx={{ fontSize: "14px", margin: "0" }}
                >
                  Total: {totalActiveCount.total} | Active:{" "}
                  {totalActiveCount.active}
                </Typography>
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
              >
                <span>{t("ASDLead")}</span>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <TextField
                    name="ASDLead"
                    value={asdData}
                    size="small"
                    className={classes.textBox}
                    onChange={(e) => {
                      setAsdData(e.target.value);
                      handleAll(e.target.value, "ASDLead");
                    }}
                  />
                </Box>
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
              >
                {t("TCIStatusOverride")}
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
              >
                {t("TCIRecoveryStatus")}
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
              >
                {t("Ticket")}
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
              >
                {t("TCIComments")}
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
              >
                {t("PERR/ExtAssigned")}
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
              >
                {t("PERR/ExtReason")}
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
              >
                {t("Build/RebuildStick")}
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
              >
                {t("WorkHours")}
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
              >
                {t("RecoveryWorkHours")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Table Rows */}
            {tciData.map((row, i) => {
              const rowKey = `${row.OracleId}-${i}`;
              return (
                <TableRow key={rowKey}>
                  <TableCell
                    align="center"
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
                    }}
                  >
                    {/* Individual Row Cells */}
                    <Grid
                      container
                      direction="row"
                      justifyContent="center"
                      columns={1}
                      alignItems="center"
                      spacing={0}
                    >
                      {renderTextCell(
                        `${row.Name} (${row.OracleId})`,
                        row.TraineeStatus,
                        row.TraineeStatus !== "Active",
                      )}
                      {row.TTECRegistrationStatus ? (
                        <Grid item xs={0.1}>
                          <Tooltip title="TTEConnect: Registered">
                            <PowerSharpIcon
                              placeholder="TTEConnect: Registered"
                              sx={{ color: "#69DE00" }}
                            />
                          </Tooltip>
                        </Grid>
                      ) : (
                        <Grid item xs={0.1}></Grid>
                      )}
                      <LastUpdateTooltip
                        AgentCommentsModifiedDate={
                          row.AgentCommentsModifiedDate
                        }
                        AgentCommentsModifiedBy={row.AgentCommentsModifiedBy}
                      >
                        <textarea
                          className={classes.agentCommentInputContainer}
                          value={row.AgentComments ? row.AgentComments : ""}
                          maxLength={4000}
                          placeholder="Max 4000 characters allowed"
                          onChange={(e) =>
                            handleIndividualText(e, row, "AgentComments")
                          }
                          style={{ width: "100%", minWidth: "350px" }}
                        ></textarea>
                      </LastUpdateTooltip>
                    </Grid>
                  </TableCell>
                  {/* Other Cells */}
                  <TableCell align="center" sx={{ padding: 0.75 }}>
                    <TextField
                      name="ASDLead"
                      className={classes.textBox}
                      value={row.ASDLead === null ? "" : row.ASDLead}
                      onChange={(e) => handleIndividualText(e, row, "ASDLead")}
                      size="small"
                    />
                  </TableCell>

                  <TableCell align="center" sx={{ padding: 0 }}>
                    <span
                      className={
                        row.OverrideTCI === true
                          ? "text-decoration-line-bulk-default"
                          : null
                      }
                    >
                      {row.TCIDisposition}
                    </span>
                    <br />
                    {row.OverrideTCI === true ? (
                      <Box component={"span"} sx={{ color: "#3299FF" }}>
                        Pass (Override)
                      </Box>
                    ) : null}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: 0 }}>
                    {row.TCIRecoveryDisposition}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: 0 }}>
                    {row.ticketno}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ padding: 0, width: 100 }}
                    dangerouslySetInnerHTML={{
                      __html: row.Comments,
                    }}
                  ></TableCell>
                  <TableCell align="center">
                    <Checkbox
                      name="PERRExtAssigned"
                      checked={row.PERRExtAssigned}
                      onChange={(e) => handleChangeChecked(e, row, i)}
                      sx={{
                        padding: 0,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      name="PERRExtReason"
                      className={classes.textBoxMedium}
                      size="small"
                      value={
                        row.PERRExtReason === null ? "" : row.PERRExtReason
                      }
                      onChange={(e) =>
                        handleIndividualText(e, row, "PERRExtReason")
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      name="BuildRebuildStick"
                      className={classes.textBoxMedium}
                      size="small"
                      value={
                        row.BuildRebuildStick === null
                          ? ""
                          : row.BuildRebuildStick
                      }
                      onChange={(e) =>
                        handleIndividualText(e, row, "BuildRebuildStick")
                      }
                    />
                  </TableCell>

                  <TableCell align="center">
                    <TextField
                      name="WorkedHoursTCI"
                      className={classes.textBoxSmall}
                      size="small"
                      value={
                        row.WorkedHoursTCI === null ? "" : row.WorkedHoursTCI
                      }
                      onChange={(e) =>
                        handleIndividualText(e, row, "WorkedHoursTCI")
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      name="WorkedHoursTCIRecovery"
                      className={classes.textBoxSmall}
                      size="small"
                      value={
                        row.WorkedHoursTCIRecovery === null
                          ? ""
                          : row.WorkedHoursTCIRecovery
                      }
                      onChange={(e) =>
                        handleIndividualText(e, row, "WorkedHoursTCIRecovery")
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* SnackBar Component */}
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
      {/* Buttons */}
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          paddingTop: 2,
          gap: 1,
        }}
      >
        <CustomOutlinedButton variant="outlined" onClick={navigateBack}>
          {t("CANCEL")}
        </CustomOutlinedButton>
        <CustomButton onClick={(e) => saveHandler(e)}>{t("SAVE")}</CustomButton>
        <CustomButton onClick={(e) => saveCloseHandler(e)}>
          {t("SAVE+CLOSE")}
        </CustomButton>
      </Grid>
    </>
  );
};

TCIV2.prototype = {
  filterStatus: PropTypes.string,
  closeLoader: PropTypes.func,
  resetStatus: PropTypes.bool,
  onChange: PropTypes.func,
};

export default TCIV2;
